import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Button } from '@mui/material';
import { motion } from 'framer-motion';
import {
  ArrowLeft, FolderOpen, Settings2, EyeOff, MessageSquare, Wrench, PieChart,
  Eye, Zap, BarChart2, Users, Rocket, Target, ArrowRight,
} from 'lucide-react';
import { ROUTES } from '@/router/routes';

// ─── DATA ────────────────────────────────────────────────────────────────────

const SHIFT_ROWS = [
  {
    before: {
      Icon: FolderOpen,
      title: 'Information in Silos',
      body: 'Critical data lives in disconnected spreadsheets, folders, and departments.',
    },
    after: {
      Icon: Eye,
      title: 'Complete Institutional Visibility',
      body: 'Every department works from one connected source of truth in real time.',
    },
  },
  {
    before: {
      Icon: Settings2,
      title: 'Processes That Drain Time',
      body: 'Manual workflows consume your team\'s capacity and slow down everything.',
    },
    after: {
      Icon: Zap,
      title: 'Operational Excellence',
      body: 'Automated workflows save hours every day and unlock your team\'s true potential.',
    },
  },
  {
    before: {
      Icon: EyeOff,
      title: 'Blind Spots in Leadership',
      body: 'Outdated reports and scattered data lead to reactive instead of proactive decisions.',
    },
    after: {
      Icon: BarChart2,
      title: 'Faster Decision Making',
      body: 'Live dashboards and real-time insights help leaders act with confidence.',
    },
  },
  {
    before: {
      Icon: MessageSquare,
      title: 'Communication Gaps',
      body: 'Parents, teachers, students and admin operate in fragmented channels.',
    },
    after: {
      Icon: Users,
      title: 'Stronger Stakeholder Engagement',
      body: 'Everyone stays informed, aligned and connected through one unified platform.',
    },
  },
  {
    before: {
      Icon: Wrench,
      title: 'Growth Without Infrastructure',
      body: 'Expanding campuses or programs exposes how fragile disconnected systems truly are.',
    },
    after: {
      Icon: Rocket,
      title: 'Sustainable & Scalable Growth',
      body: 'One platform. Any size. Scale to any campus or program without adding complexity.',
    },
  },
  {
    before: {
      Icon: PieChart,
      title: 'Data Without Direction',
      body: 'Large amounts of data exist but don\'t translate into strategic action.',
    },
    after: {
      Icon: Target,
      title: 'Institutional Agility',
      body: 'Use the right data, at the right time, to adapt faster and lead with clarity.',
    },
  },
];

// ─── ANIMATION HELPERS ────────────────────────────────────────────────────────

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 22 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.52, delay, ease: [0.22, 1, 0.36, 1] as number[] },
});

// ─── COMPONENT ────────────────────────────────────────────────────────────────

export default function TransformationPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(150deg, #FFF9F2 0%, #F8F5FF 55%, #EEF2FF 100%)',
      fontFamily: '"DM Sans", sans-serif',
      position: 'relative',
      overflow: 'hidden',
    }}>

      {/* ── Decorative blobs ── */}
      <Box sx={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }}>
        <Box sx={{ position: 'absolute', top: '-10%', right: '-8%', width: '480px', height: '480px', background: 'radial-gradient(circle, rgba(124,99,216,0.08), transparent 70%)', borderRadius: '50%' }} />
        <Box sx={{ position: 'absolute', bottom: '-8%', left: '-6%', width: '380px', height: '380px', background: 'radial-gradient(circle, rgba(251,146,60,0.06), transparent 70%)', borderRadius: '50%' }} />
        <Box sx={{ position: 'absolute', top: '45%', left: '50%', transform: 'translate(-50%,-50%)', width: '800px', height: '300px', background: 'radial-gradient(ellipse, rgba(124,99,216,0.04), transparent 70%)' }} />
      </Box>

      {/* ── Fixed mini nav ── */}
      <Box sx={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        bgcolor: 'rgba(255,249,242,0.88)', backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(124,99,216,0.1)',
        px: { xs: 2, sm: 4, md: 6 }, height: '64px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Button
          onClick={() => navigate(ROUTES.PUBLIC.HOME)}
          startIcon={<ArrowLeft size={15} />}
          sx={{
            color: '#7C63D8', fontSize: '13px', fontWeight: 500,
            fontFamily: '"DM Sans", sans-serif', minWidth: 0,
            '&:hover': { color: '#6B52D1', bgcolor: 'transparent' },
          }}
        >
          Home
        </Button>

        <Typography sx={{
          fontFamily: '"Syne", sans-serif', fontWeight: 800,
          fontSize: '18px', color: '#111827', letterSpacing: '-0.3px',
          userSelect: 'none',
        }}>
          Skolar<Box component="span" sx={{ color: '#7C63D8' }}>Minds</Box>
        </Typography>

        <Button
          onClick={() => navigate(ROUTES.PUBLIC.HOME, { state: { openDemo: true } })}
          sx={{
            px: { xs: '14px', sm: '20px' }, py: '8px',
            bgcolor: '#7C63D8', borderRadius: '8px',
            color: '#fff', fontSize: '13px', fontWeight: 500,
            fontFamily: '"DM Sans", sans-serif',
            '&:hover': { bgcolor: '#6B52D1' },
          }}
        >
          Book a Demo
        </Button>
      </Box>

      {/* ── Page content ── */}
      <Container maxWidth="md" sx={{ pt: '76px', pb: '56px', position: 'relative', zIndex: 1 }}>

        {/* ── Hero ── */}
        <Box sx={{ textAlign: 'center', pt: 2, mb: 4 }}>
          <motion.div {...fadeUp(0)}>
            <Box sx={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              px: '12px', py: '5px', mb: '18px',
              border: '1px solid rgba(124,99,216,0.28)', borderRadius: '100px',
              bgcolor: 'rgba(124,99,216,0.06)',
              fontSize: '10px', fontWeight: 700, letterSpacing: '0.16em',
              textTransform: 'uppercase', color: '#7C63D8',
              fontFamily: '"DM Sans", sans-serif',
            }}>
              FROM COMPLEXITY TO CLARITY
            </Box>
          </motion.div>

          <motion.div {...fadeUp(0.08)}>
            <Typography sx={{
              fontFamily: '"Syne", sans-serif', fontWeight: 800,
              fontSize: 'clamp(22px, 2.8vw, 38px)', color: '#111827',
              lineHeight: 1.12, letterSpacing: '-1px', mb: '12px',
            }}>
              The Shift Every Modern<br />Institution Deserves
            </Typography>
          </motion.div>

          <motion.div {...fadeUp(0.16)}>
            <Typography sx={{
              fontSize: '14px', color: '#475569',
              fontWeight: 300, lineHeight: 1.7, maxWidth: '460px', mx: 'auto',
            }}>
              See how disconnected operations become a connected,{' '}
              intelligent institution with{' '}
              <Box component="span" sx={{ color: '#7C63D8', fontWeight: 600 }}>SkolarMinds</Box>.
            </Typography>
          </motion.div>
        </Box>

        {/* ── Column headers ── */}
        <motion.div {...fadeUp(0.24)}>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 56px 1fr',
            gap: '10px',
            mb: 2,
          }}>
            {/* Left header */}
            <Box sx={{
              textAlign: 'center', py: '9px', px: '14px',
              bgcolor: '#FFF3EC',
              border: '1.5px solid rgba(251,146,60,0.22)',
              borderRadius: '10px',
            }}>
              <Typography sx={{ fontSize: '10px', fontWeight: 800, letterSpacing: '0.16em', color: '#EA580C', textTransform: 'uppercase', mb: '2px' }}>
                Current Reality
              </Typography>
              <Typography sx={{ fontSize: '11.5px', color: '#92400E', fontWeight: 400 }}>
                Disconnected. Manual. Reactive.
              </Typography>
            </Box>

            {/* Spacer */}
            <Box />

            {/* Right header */}
            <Box sx={{
              textAlign: 'center', py: '9px', px: '14px',
              bgcolor: '#F3F0FF',
              border: '1.5px solid rgba(124,99,216,0.22)',
              borderRadius: '10px',
            }}>
              <Typography sx={{ fontSize: '10px', fontWeight: 800, letterSpacing: '0.16em', color: '#7C63D8', textTransform: 'uppercase', mb: '2px' }}>
                Powered by SkolarMinds
              </Typography>
              <Typography sx={{ fontSize: '11.5px', color: '#5B4DB5', fontWeight: 400 }}>
                Connected. Automated. Proactive.
              </Typography>
            </Box>
          </Box>
        </motion.div>

        {/* ── Comparison rows ── */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {SHIFT_ROWS.map(({ before, after }, i) => (
            <motion.div
              key={before.title}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.28 + i * 0.06, ease: [0.22, 1, 0.36, 1] }}
            >
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 56px 1fr' },
                gap: '8px',
                alignItems: 'center',
              }}>

                {/* ── Before card ── */}
                <Box
                  component={motion.div}
                  whileHover={{ y: -2, transition: { duration: 0.18 } }}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    p: '12px 16px',
                    bgcolor: '#fff',
                    border: '1.5px solid rgba(251,146,60,0.14)',
                    borderLeft: '3px solid rgba(251,146,60,0.55)',
                    borderRadius: '12px',
                    cursor: 'default',
                    transition: 'box-shadow 0.25s, border-color 0.25s',
                    '&:hover': {
                      boxShadow: '0 6px 20px rgba(251,146,60,0.09)',
                      borderColor: 'rgba(251,146,60,0.28)',
                    },
                    '&:hover .shift-icon-before': {
                      bgcolor: '#FED7AA',
                      transform: 'scale(1.12) rotate(-5deg)',
                    },
                  }}
                >
                  <Box className="shift-icon-before" sx={{
                    width: 36, height: 36, borderRadius: '9px',
                    bgcolor: '#FFF0E6',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                    transition: 'all 0.22s ease',
                  }}>
                    <before.Icon size={16} color="#EA580C" />
                  </Box>
                  <Box>
                    <Typography sx={{
                      fontFamily: '"Syne", sans-serif', fontWeight: 700,
                      fontSize: '13.5px', color: '#1C1917',
                      mb: '3px', lineHeight: 1.25, letterSpacing: '-0.2px',
                    }}>
                      {before.title}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#78716C', lineHeight: 1.6 }}>
                      {before.body}
                    </Typography>
                  </Box>
                </Box>

                {/* ── Center arrow connector ── */}
                <Box sx={{
                  display: { xs: 'none', md: 'flex' },
                  flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '3px',
                }}>
                  <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'rgba(124,99,216,0.3)' }} />
                  <Box sx={{
                    width: '2px', height: '10px',
                    backgroundImage: 'repeating-linear-gradient(to bottom, rgba(124,99,216,0.3) 0px, rgba(124,99,216,0.3) 3px, transparent 3px, transparent 6px)',
                  }} />
                  <Box
                    component={motion.div}
                    animate={{ scale: [1, 1.08, 1] }}
                    transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut', delay: i * 0.18 }}
                    sx={{
                      width: 26, height: 26, borderRadius: '50%',
                      bgcolor: '#7C63D8',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: '0 0 0 4px rgba(124,99,216,0.1)',
                      flexShrink: 0,
                    }}
                  >
                    <ArrowRight size={13} color="#fff" />
                  </Box>
                  <Box sx={{
                    width: '2px', height: '10px',
                    backgroundImage: 'repeating-linear-gradient(to bottom, rgba(124,99,216,0.3) 0px, rgba(124,99,216,0.3) 3px, transparent 3px, transparent 6px)',
                  }} />
                  <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'rgba(124,99,216,0.3)' }} />
                </Box>

                {/* Mobile arrow (xs only) */}
                <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center' }}>
                  <Box sx={{ width: 26, height: 26, borderRadius: '50%', bgcolor: '#7C63D8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ArrowRight size={12} color="#fff" style={{ transform: 'rotate(90deg)' }} />
                  </Box>
                </Box>

                {/* ── After card ── */}
                <Box
                  component={motion.div}
                  whileHover={{ y: -2, transition: { duration: 0.18 } }}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    p: '12px 16px',
                    bgcolor: '#fff',
                    border: '1.5px solid rgba(124,99,216,0.14)',
                    borderLeft: '3px solid rgba(124,99,216,0.55)',
                    borderRadius: '12px',
                    cursor: 'default',
                    transition: 'box-shadow 0.25s, border-color 0.25s',
                    '&:hover': {
                      boxShadow: '0 6px 20px rgba(124,99,216,0.10)',
                      borderColor: 'rgba(124,99,216,0.30)',
                    },
                    '&:hover .shift-icon-after': {
                      bgcolor: '#DDD6FE',
                      transform: 'scale(1.12) rotate(5deg)',
                    },
                  }}
                >
                  <Box className="shift-icon-after" sx={{
                    width: 36, height: 36, borderRadius: '9px',
                    bgcolor: '#EDE9FF',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                    transition: 'all 0.22s ease',
                  }}>
                    <after.Icon size={16} color="#7C63D8" />
                  </Box>
                  <Box>
                    <Typography sx={{
                      fontFamily: '"Syne", sans-serif', fontWeight: 700,
                      fontSize: '13.5px', color: '#1C1917',
                      mb: '3px', lineHeight: 1.25, letterSpacing: '-0.2px',
                    }}>
                      {after.title}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#78716C', lineHeight: 1.6 }}>
                      {after.body}
                    </Typography>
                  </Box>
                </Box>

              </Box>
            </motion.div>
          ))}
        </Box>

        {/* ── Bottom CTA ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        >
          <Box sx={{
            textAlign: 'center', mt: 6, pt: 4,
            borderTop: '1px solid rgba(124,99,216,0.12)',
          }}>
            <Typography sx={{
              fontFamily: '"Syne", sans-serif', fontWeight: 800,
              fontSize: 'clamp(20px, 2.5vw, 32px)', color: '#111827',
              mb: '10px', letterSpacing: '-0.8px',
            }}>
              Ready to Make the Shift?
            </Typography>
            <Typography sx={{
              fontSize: '14px', color: '#6B7280',
              mb: '28px', fontWeight: 300, lineHeight: 1.65,
            }}>
              Join institutions that have already made the transition — and never looked back.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                onClick={() => navigate(ROUTES.PUBLIC.HOME, { state: { openDemo: true } })}
                sx={{
                  px: '28px', py: '11px', bgcolor: '#7C63D8', color: '#fff',
                  borderRadius: '10px', fontSize: '14px', fontWeight: 600,
                  fontFamily: '"DM Sans", sans-serif',
                  boxShadow: '0 4px 16px rgba(124,99,216,0.28)',
                  '&:hover': {
                    bgcolor: '#6B52D1', transform: 'translateY(-2px)',
                    boxShadow: '0 12px 32px rgba(124,99,216,0.38)',
                  },
                  transition: 'all 0.25s',
                }}
              >
                Book a Demo
              </Button>
              <Button
                onClick={() => navigate(ROUTES.PUBLIC.HOME)}
                sx={{
                  px: '28px', py: '11px', bgcolor: 'transparent',
                  color: '#475569',
                  borderRadius: '10px', fontSize: '14px', fontWeight: 500,
                  border: '1.5px solid rgba(124,99,216,0.22)',
                  fontFamily: '"DM Sans", sans-serif',
                  '&:hover': {
                    bgcolor: 'rgba(124,99,216,0.05)', color: '#7C63D8',
                    borderColor: 'rgba(124,99,216,0.45)',
                  },
                  transition: 'all 0.25s',
                }}
              >
                Explore the Platform
              </Button>
            </Box>
          </Box>
        </motion.div>

      </Container>
    </Box>
  );
}
